import type { FC } from 'react';
import { useCallback, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import {
  BiData,
  BuyCoreButtonBiEvent,
  StudioBatchUpdateBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import BuyCoreBrandedButton from '@wix/da-shared-react/pkg/Button/core/dsPresets/BuyCoreBrandedButton';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { Url } from '@wix/da-url';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import ScheduleTimeForm from '@wix/da-submit-shared/pkg/common/ScheduleTimeForm';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';

import s from './ScheduleBatchModal.scss';

export interface Props {
  batchSchedule: (scheduleTs: number) => void;
  requestClose: () => void;
  selectedCount?: number;
  requiresCore?: boolean;
}

const ScheduleBatchModal: FC<Props> = ({
  batchSchedule,
  requestClose,
  selectedCount = 0,
  requiresCore = true,
}) => {
  const { t } = useTranslation();
  const { hasCoreMembership } = useContext(SessionContext);
  const [selectedTs, setSelectedTs] = useState<number>();
  const canSave = hasCoreMembership || (!requiresCore && selectedCount < 2);

  const handleSave = useCallback(() => {
    if (selectedTs) {
      batchSchedule(selectedTs);
      requestClose();
    }
  }, [batchSchedule, requestClose, selectedTs]);

  const coreUpgradeBiData = BiData<BuyCoreButtonBiEvent>({
    evid: 375,
    referral_info: ReferralType.STUDIO_BATCH_SCHEDULE,
  });

  const biData =
    coreUpgradeBiData ||
    BiData<BuyCoreButtonBiEvent>({
      evid: 375,
      referral_info: ReferralType.STUDIO_BATCH_ACTION,
    });

  return (
    <Modal
      className={s['wrapper']}
      layoutClassName={s['layout']}
      contentClassName={s['content']}
      heading={t('studio.batchActions.title.schedule')}
      onRequestClose={requestClose}
      showHeaderBorder
      showFooterBorder
      shouldCloseOnOverlayClick
      isOpen
      withScrollableContentWrapper
      footerRight={[
        <TextButton
          onClick={requestClose}
          size="medium"
          variant="main"
          key="footer-cancel"
        >
          {t('common.cancel')}
        </TextButton>,
        canSave ? (
          <BrandedButton
            onClick={handleSave}
            disabled={!selectedTs}
            size="medium"
            variant="brand-green"
            key="footer-save"
            biData={BiData<StudioBatchUpdateBiEvent>({
              evid: 12,
              items_count: selectedCount,
              update_type: 'schedule',
            })}
          >
            {t('common.save')}
          </BrandedButton>
        ) : (
          <SimpleTooltip
            text={t('studio.batchActions.cta.saveNonCore.tooltip', {
              postProcess: 'reactPostprocessor',
              upgradeLink: (
                <PlainButtonOrLink
                  className={s['tooltip-upgrade-link']}
                  href={Url.coreMembershipLink()}
                  target="_blank"
                  biData={BiData<BuyCoreButtonBiEvent>({
                    evid: 375,
                    referral_info: ReferralType.STUDIO_BATCH_SCHEDULE,
                  })}
                >
                  {t('studio.table.header.submitNonCore.upgradeLink')}
                </PlainButtonOrLink>
              ),
            })}
            key="footer-save-noncore"
            usePortal
          >
            <BuyCoreBrandedButton size="medium" biData={biData}>
              {t('studio.batchActions.cta.scheduleNonCore')}
            </BuyCoreBrandedButton>
          </SimpleTooltip>
        ),
      ]}
    >
      <div className={s['form-container']}>
        <div className={s['details']}>
          {t('studio.batchActions.subtitle.edit_schedule', {
            count: selectedCount,
          })}
        </div>
        <ScheduleTimeForm onChange={setSelectedTs} />
      </div>
    </Modal>
  );
};

ScheduleBatchModal.displayName = 'ScheduleBatchModal';

export default ScheduleBatchModal;
